// ✅ Refactored DeputyUser to follow TheatresPage pattern with DataTableThree + Edit Modal

import React, { useState, useMemo, useEffect } from "react";
import {
  Box,
  Typography,
  Avatar,
  Tooltip,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  FormControl,
  FormControlLabel,
  Grid,
  IconButton,
  InputAdornment,
  Paper,
  TextField,
  InputLabel,
  MenuItem,
  Select,
  LinearProgress,
  Card,
  Stack,
  Alert,
  Divider,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import {
  Edit,
  Block,
  Visibility,
  VisibilityOff,
  Add,
  CheckCircle,
  ImageOutlined,
  DeleteOutlineOutlined,
  DeleteOutlined,
  PendingActions,
  LockReset,
  ManageAccounts,
  AlternateEmail,
  SearchRounded,
  RestartAltRounded,
  PictureAsPdfRounded,
  TableChartRounded,
} from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import { format } from "date-fns";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import * as XLSX from "xlsx";
import { DefaultPage } from "../../layouts/default-page";
import {
  useAddUserMutation,
  useBlockUserMutation,
  useGetDeputyUsersQuery,
  useLazyGetDeputyUsersQuery,
  useUpdateDeputyUserMutation,
  useChangeDeputyPasswordMutation,
  useChangeUserEmailMutation,
} from "../../services/user.query";
import ConfirmationDialogue from "../../layouts/confirmation-dialogue";
import { LoadingButton } from "@mui/lab";
import { useFormik } from "formik";
import { Notify } from "../../layouts/notify";
import { SignUp } from "../../services/AuthService";
import * as Yup from "yup";
import { DataTableFive } from "../../layouts/layout2/data-table-five";
import { StatusChip } from "../common/StatusChip";
import { RowActions } from "../common/RowActions";
import { Block as BlockIcon } from "@mui/icons-material";
import { ExportLoadingBackdrop } from "../common/ExportLoadingBackdrop";
import { fetchAllInBatches, mapRowsInChunks, yieldToMainThread } from "../../utils/exportUtils";

type FormValues = {
  email: string;
  password: string;
  phone_number: string;
  name: string;
  last_name: string;
  deputy_current_year: string;
  deputy_control_number: string;
  is_waved_report_enable: boolean;
  status: "VERIFIED" | "BLOCKED" | "PENDING" | "REJECTED";
  profile_picture: string | null;         // allow null
  profile_pic_file: File | null;          // <— this is the missing field
  card_issued_date: string;               // yyyy-mm-dd
  deputy_endorser: string;
  delete_request: "PENDING" | "FALSE" | "APPROVED";
};

const CONTROL_HEIGHT = 36;

const getValidationSchema = (isEdit: boolean) =>
  Yup.object({
    name: Yup.string().required("Firstname is required"),
    last_name: Yup.string().required("Lastname is required"),
    email: Yup.string().email("Invalid email address").required("Email is required"),
    password: isEdit
      ? Yup.string().notRequired()
      : Yup.string().min(8, "Password must be at least 8 characters long").required("Password is required"),
    phone_number: Yup.string()
      .matches(/^[0-9]+$/, "Phone number must be only digits")
      .min(10, "Phone number must be at least 10 digits long")
      .max(15, "Phone number must not exceed 15 digits"),
    deputy_current_year: Yup.string()
      .required("Deputy Year is required")
      .matches(/^\d{4}$/, "Deputy Year must be a 4-digit year")
      .test(
        "valid-year-range",
        "Deputy Year must be between 2024 and next year",
        (value) => {
          if (!value) return false;
          const year = Number(value);
          const currentYear = new Date().getFullYear();
          return year >= 2024 && year <= currentYear + 1;
        }
      ),
    deputy_control_number: Yup.string().required("Deputy Control Number is required"),
    is_waved_report_enable: Yup.boolean().required("Report Exemption Setting is required"),

    // keep URL checks (for edit preview/current value)
    profile_picture: Yup.string()
      .nullable()
      .transform((val, orig) => (orig === "" ? null : val))
      .test(
        "is-url-or-blob",
        "Must be a valid image URL",
        (v) =>
          !v ||
          /^https?:\/\//i.test(v) ||
          v.startsWith("blob:") ||
          v.startsWith("data:image/")
      ),

    // 👇 Required only when adding
    profile_pic_file: (isEdit
      ? Yup.mixed<File>().nullable()
      : Yup.mixed<File>()
        .nullable()
        .required("ID Picture is required")
    )
      .test("fileType", "Only PNG/JPG/WebP allowed", (f) => !f || ["image/png", "image/jpeg", "image/webp"].includes(f.type))
      .test("fileSize", "Max 2MB", (f) => !f || f.size <= 2 * 1024 * 1024),

    card_issued_date: isEdit ? Yup.string().notRequired() : Yup.string().required("Card Issued Date is required"),
    deputy_endorser: Yup.string()
  });




export default function DeputyUserPage() {
  const navigate = useNavigate();
  const CURRENT_YEAR = new Date().getFullYear();

  const [parentPage, setParentPage] = useState(1);
  const rowsPerParentPage = 100;
  const [selectedYear, setSelectedYear] = useState<number>(CURRENT_YEAR);
  const [blockState, setBlockState] = useState(false);
  const [openConfirm, setOpenConfirm] = useState(false);
  const [selectedItem, setSelectedItem] = useState<any>(null);
  const [editMode, setEditMode] = useState<boolean>(false);
  const [editOpen, setEditOpen] = useState(false);
  const [editUser, setEditUser] = useState<any>(null);
  const [currentUser, setCurrentUser] = useState<any>(null);
  const [openAddModal, setOpenAddModal] = useState<boolean>(false);
  const [showPassword, setShowPassword] = useState(false);
  const [searchInput, setSearchInput] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [picPreviewUrl, setPicPreviewUrl] = useState<string>("");
  const [picDragOver, setPicDragOver] = useState(false);
  const [picUploading, setPicUploading] = useState(false); // visual only
  const fileInputRef = React.useRef<HTMLInputElement | null>(null);
  const [sortBy, setSortBy] = useState<'CONTROL' | 'LAST_NAME'>('CONTROL');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc');

  // Row action selector
  const [userActionOpen, setUserActionOpen] = useState(false);
  const [actionUser, setActionUser] = useState<any>(null);

  // Change password dialog
  const [changePasswordOpen, setChangePasswordOpen] = useState(false);
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showChangePassword, setShowChangePassword] = useState(false);

  // Change email dialog
  const [changeEmailOpen, setChangeEmailOpen] = useState(false);
  const [newEmail, setNewEmail] = useState("");
  const [confirmEmail, setConfirmEmail] = useState("");

  const handleTogglePasswordVisibility = () => {
    setShowPassword((prev) => !prev);
  };

  const yearOptions = useMemo(() => {
    const startYear = 2024;
    const years: number[] = [];
    for (let y = CURRENT_YEAR; y >= startYear; y--) {
      years.push(y);
    }
    return years;
  }, []);

  const start = (parentPage - 1) * rowsPerParentPage;
  const end = start + rowsPerParentPage - 1;

  const {
    data,
    isLoading,
    isFetching,
    isError,
    isSuccess,
    refetch,
  } = useGetDeputyUsersQuery({
    page: parentPage,
    perPage: rowsPerParentPage,
    selectedYear,
    searchTerm,
    sortBy,
    sortDir,
  });


  const [addDeputyUser, { isLoading: addDeputyUserLoading }] =
    useAddUserMutation();
  const [updateDeputyuser, { isLoading: updating }] =
    useUpdateDeputyUserMutation();
  const [changeDeputyPassword, { isLoading: changingPassword }] =
    useChangeDeputyPasswordMutation();
  const [changeUserEmail, { isLoading: changingEmail }] =
    useChangeUserEmailMutation();

  const [triggerFetchAllDeputies] = useLazyGetDeputyUsersQuery();
  const [isExporting, setIsExporting] = useState(false);
  const [exportMessage, setExportMessage] = useState("");
  const [exportProgress, setExportProgress] = useState<number | null>(null);

  const triggerSearch = () => {
    setParentPage(1);        // optional: reset paging on new search
    setSearchTerm(searchInput.trim());
  };

  const clearSearch = () => {
    setSearchInput("");
    setSearchTerm("");
    setParentPage(1);
  };

  const onSearchKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") triggerSearch();
  };

  const fetchAllRegisteredDeputies = async () => {
    return fetchAllInBatches<any>({
      batchSize: 1000,
      knownTotal: Number(data?.count || 0),
      fetchBatch: async (_offset, limit, batchPage) => {
        const res = await triggerFetchAllDeputies(
          {
            page: batchPage,
            perPage: limit,
            selectedYear,
            searchTerm,
            sortBy,
            sortDir,
          },
          false
        ).unwrap();
        return {
          rows: res?.list || [],
          total: res?.count ?? Number(data?.count || 0),
        };
      },
      onProgress: (fetched, total, percent) => {
        setExportProgress(percent);
        if (total && total > 0) {
          setExportMessage(
            `Fetching registered deputies: ${fetched.toLocaleString()} of ${total.toLocaleString()} deputies...`
          );
        } else {
          setExportMessage(
            `Fetching registered deputies: ${fetched.toLocaleString()} deputies loaded...`
          );
        }
      },
    });
  };

  const handleExportPDF = async () => {
    setIsExporting(true);
    setExportProgress(0);
    setExportMessage("Preparing to fetch all matching registered deputies...");
    try {
      const allRows = await fetchAllRegisteredDeputies();
      if (allRows.length === 0) {
        Notify("info", "No records found to export.");
        return;
      }

      setExportProgress(88);
      setExportMessage(`Formatting ${allRows.length.toLocaleString()} deputies for PDF...`);
      await yieldToMainThread(30);

      const tableRows = await mapRowsInChunks(
        allRows,
        (item: any, idx: number) => [
          idx + 1,
          item.deputy_control_number || "—",
          `${item.last_name || ""}, ${item.name || ""}`.trim() || "—",
          item.email || "—",
          item.phone_number || "—",
          item.deputy_endorser || "—",
          item.deputy_current_year || selectedYear || "—",
          item.status || "VERIFIED",
        ],
        {
          chunkSize: 1000,
          startPercent: 88,
          endPercent: 95,
          onProgress: (processed, total, percent) => {
            setExportProgress(percent);
            setExportMessage(
              `Preparing PDF rows: ${processed.toLocaleString()} of ${total.toLocaleString()}...`
            );
          },
        }
      );

      setExportProgress(96);
      setExportMessage(`Rendering PDF document (${allRows.length.toLocaleString()} deputies)...`);
      await yieldToMainThread(40);

      const doc = new jsPDF("landscape", "mm", "a4");
      doc.setFontSize(14);
      doc.setTextColor(0, 114, 188);
      doc.text("MTRCB - Registered Deputy Users Directory", 14, 15);

      doc.setFontSize(9);
      doc.setTextColor(100, 116, 139);
      doc.text(
        `Generated on: ${format(new Date(), "MMMM dd, yyyy · hh:mm a")} | Year: ${selectedYear} | Total Deputies: ${allRows.length.toLocaleString()}`,
        14,
        22
      );

      const tableColumn = [
        "#",
        "Control Number",
        "Deputy Name",
        "Email Address",
        "Mobile Number",
        "Endorser",
        "Year",
        "Status",
      ];

      autoTable(doc, {
        head: [tableColumn],
        body: tableRows,
        startY: 27,
        theme: "grid",
        headStyles: {
          fillColor: [0, 114, 188],
          textColor: [255, 255, 255],
          fontStyle: "bold",
          fontSize: 8.5,
        },
        alternateRowStyles: { fillColor: [248, 250, 252] },
        styles: {
          fontSize: 8,
          cellPadding: 2.5,
          textColor: [15, 23, 42],
          overflow: "linebreak",
        },
        margin: { top: 12, left: 14, right: 14, bottom: 14 },
      });

      const totalPages = (doc as any).internal.getNumberOfPages();
      const pageWidth = doc.internal.pageSize.getWidth();
      const pageHeight = doc.internal.pageSize.getHeight();
      for (let i = 1; i <= totalPages; i++) {
        doc.setPage(i);
        doc.setFontSize(7.5);
        doc.setTextColor(148, 163, 184);
        doc.text(
          `Page ${i} of ${totalPages}`,
          pageWidth - 14,
          pageHeight - 6,
          { align: "right" }
        );
      }

      setExportProgress(100);
      setExportMessage("Download starting...");
      await yieldToMainThread(20);

      doc.save(`MTRCB_Deputies_${selectedYear}_${format(new Date(), "yyyyMMdd_HHmm")}.pdf`);
      Notify("success", `Exported all ${allRows.length.toLocaleString()} deputies to PDF.`);
    } catch (err: any) {
      console.error("PDF export error:", err);
      Notify("error", err?.message || "Failed to generate PDF export.");
    } finally {
      setIsExporting(false);
      setExportProgress(null);
    }
  };

  const handleExportExcel = async () => {
    setIsExporting(true);
    setExportProgress(0);
    setExportMessage("Preparing to fetch all matching registered deputies...");
    try {
      const allRows = await fetchAllRegisteredDeputies();
      if (allRows.length === 0) {
        Notify("info", "No records found to export.");
        return;
      }

      setExportProgress(88);
      setExportMessage(`Formatting ${allRows.length.toLocaleString()} deputies for Excel...`);
      await yieldToMainThread(30);

      const rows = await mapRowsInChunks(
        allRows,
        (item: any, idx: number) => ({
          "No.": idx + 1,
          "Control Number": item.deputy_control_number || "—",
          "Last Name": item.last_name || "—",
          "First Name": item.name || "—",
          "Email Address": item.email || "—",
          "Mobile Number": item.phone_number || "—",
          "Endorser": item.deputy_endorser || "—",
          "Year": item.deputy_current_year || selectedYear || "—",
          "Status": item.status || "VERIFIED",
        }),
        {
          chunkSize: 1000,
          startPercent: 88,
          endPercent: 95,
          onProgress: (processed, total, percent) => {
            setExportProgress(percent);
            setExportMessage(
              `Preparing Excel rows: ${processed.toLocaleString()} of ${total.toLocaleString()}...`
            );
          },
        }
      );

      setExportProgress(96);
      setExportMessage(`Writing Excel workbook (${allRows.length.toLocaleString()} deputies)...`);
      await yieldToMainThread(40);

      const ws = XLSX.utils.json_to_sheet(rows);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "Deputies");

      ws["!cols"] = [
        { wch: 6 },
        { wch: 18 },
        { wch: 20 },
        { wch: 20 },
        { wch: 32 },
        { wch: 18 },
        { wch: 24 },
        { wch: 10 },
        { wch: 14 },
      ];

      XLSX.writeFile(wb, `MTRCB_Deputies_${selectedYear}_${format(new Date(), "yyyyMMdd_HHmm")}.xlsx`);
      setExportProgress(100);
      setExportMessage("Download complete!");
      await yieldToMainThread(20);
      Notify("success", `Exported all ${allRows.length.toLocaleString()} deputies to Excel.`);
    } catch (err: any) {
      console.error("Excel export error:", err);
      Notify("error", err?.message || "Failed to generate Excel export.");
    } finally {
      setIsExporting(false);
      setExportProgress(null);
    }
  };

  const openAddEditModal = (user: any = null) => {
    const toYMD = (d: any) => {
      if (!d) return "";
      const date = typeof d === "string" ? new Date(d) : d;
      if (isNaN(date as any)) return "";
      return new Date(date).toISOString().slice(0, 10);
    };

    if (user) {
      setEditMode(true);
      setCurrentUser(user);

      formik.setValues({
        email: user.email || "",
        password: "",
        phone_number: user.phone_number || "",
        name: user.name || "",
        last_name: user.last_name || "",
        deputy_current_year: String(user.deputy_current_year ?? ""),
        deputy_control_number: String(user.deputy_control_number ?? ""),
        is_waved_report_enable: !!user.is_waved_report_enable,
        status: (user.status as FormValues["status"]) || "VERIFIED",

        // 👇 make sure both of these exist
        profile_picture: user.profile_picture || null,
        profile_pic_file: null,

        card_issued_date: toYMD(user.card_issued_date),
        deputy_endorser: user.deputy_endorser || "",
        delete_request: user.delete_request || "FALSE",
      });

      setPicPreviewUrl(user.profile_picture || "");
    } else {
      setEditMode(false);
      formik.resetForm();
      setPicPreviewUrl("");
    }
    setOpenAddModal(true);
  };



  const deputyUsers = data?.list ?? [];
  const totalDeputyCount = data?.count ?? 0;

  const [blockDeputy, { isLoading: blockLoading }] = useBlockUserMutation();

  const handlePageChange = (page: number) => setParentPage(page);
  const openBlockModal = (row: any, isBlock: boolean) => {
    setBlockState(isBlock);
    setOpenConfirm(true);
    setSelectedItem(row);
  };

  const openUserActionDialog = (row: any) => {
    setActionUser(row);
    setUserActionOpen(true);
  };

  const closeUserActionDialog = () => {
    setUserActionOpen(false);
  };

  const handleUpdateDeputyAction = () => {
    if (!actionUser) return;
    setUserActionOpen(false);
    openAddEditModal(actionUser);
  };

  const handleChangePasswordAction = () => {
    if (!actionUser) return;
    setUserActionOpen(false);
    setNewPassword("");
    setConfirmPassword("");
    setShowChangePassword(false);
    setChangePasswordOpen(true);
  };

  const handleChangeEmailAction = () => {
    if (!actionUser) return;
    setUserActionOpen(false);
    setNewEmail("");
    setConfirmEmail("");
    setChangeEmailOpen(true);
  };

  const closeChangeEmailDialog = () => {
    if (changingEmail) return;
    setChangeEmailOpen(false);
    setNewEmail("");
    setConfirmEmail("");
  };

  const submitChangeEmail = async () => {
    try {
      if (!actionUser?.user_id) {
        Notify("error", "Missing Supabase Auth user_id for this deputy.");
        return;
      }

      const emailTrimmed = newEmail.trim().toLowerCase();
      const confirmTrimmed = confirmEmail.trim().toLowerCase();

      if (!emailTrimmed || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(emailTrimmed)) {
        Notify("warning", "Please enter a valid email address.");
        return;
      }

      if (emailTrimmed !== confirmTrimmed) {
        Notify("warning", "Email confirmation does not match.");
        return;
      }

      if (emailTrimmed === (actionUser?.email || "").trim().toLowerCase()) {
        Notify("warning", "The new email must be different from the current email.");
        return;
      }

      await changeUserEmail({
        user_id: actionUser.user_id,
        new_email: emailTrimmed,
        users_id: actionUser.id,
        is_deputy: true,
        deputy_control_number: actionUser.deputy_control_number,
      }).unwrap();

      Notify("info", "Email changed successfully.");
      closeChangeEmailDialog();
    } catch (error: any) {
      console.error(error);
      Notify("error", error?.data || error?.message || "Failed to change email");
    }
  };

  const closeChangePasswordDialog = () => {
    if (changingPassword) return;
    setChangePasswordOpen(false);
    setNewPassword("");
    setConfirmPassword("");
    setShowChangePassword(false);
  };

  const submitChangePassword = async () => {
    try {
      const password = newPassword.trim();
      const confirm = confirmPassword.trim();

      if (!actionUser?.user_id) {
        Notify("error", "Missing Supabase Auth user_id for this deputy.");
        return;
      }

      if (password.length < 8) {
        Notify("warning", "Password must be at least 8 characters long.");
        return;
      }

      if (password !== confirm) {
        Notify("warning", "Password confirmation does not match.");
        return;
      }

      await changeDeputyPassword({
        user_id: actionUser.user_id,
        password,
      }).unwrap();

      Notify("info", "Password changed successfully.");
      closeChangePasswordDialog();
    } catch (error: any) {
      console.error(error);
      Notify("error", error?.data || error?.message || "Failed to change password");
    }
  };

  const closeAddEditModal = () => {
    setOpenAddModal(false);
    setCurrentUser(null);
  };

  const blockUser = async () => {
    const status = blockState ? "BLOCKED" : "VERIFIED";
    await blockDeputy({
      user_id: selectedItem.user_id,
      id: selectedItem.id,
      status,
    });
    setOpenConfirm(false);
  };

  const handleChooseClick = () => {
    fileInputRef.current?.click();
  };

  const handleFilePicked = (file?: File) => {
    if (!file) return;
    // basic front-end checks (UX only; enforce real rules later server-side)
    const isImage = file.type.startsWith("image/");
    const isLt2M = file.size <= 2 * 1024 * 1024;
    if (!isImage) {
      Notify("warning", "Please select an image file (png/jpg/webp).");
      return;
    }
    if (!isLt2M) {
      Notify("warning", "Image must be 2MB or smaller.");
      return;
    }

    setPicUploading(true);
    const localUrl = URL.createObjectURL(file);
    setPicPreviewUrl(localUrl);

    // keep selected file for later wiring (optional: store in formik)
    formik.setFieldValue("profile_pic_file", file);
    formik.setFieldValue("profile_picture", URL.createObjectURL(file)); // preview ok now
    formik.setFieldTouched("profile_pic_file", true, false);

    // simulate brief progress
    setTimeout(() => setPicUploading(false), 600);
  };

  const onInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0];
    handleFilePicked(f);
    // reset so picking the same file again re-triggers change event
    e.currentTarget.value = "";
  };

  const onDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setPicDragOver(false);
    const f = e.dataTransfer.files?.[0];
    handleFilePicked(f);
  };

  const onDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setPicDragOver(true);
  };

  const onDragLeave = () => {
    setPicDragOver(false);
  };

  const clearPreview = () => {
    setPicPreviewUrl("");
    formik.setFieldValue("profile_picture", "");
    formik.setFieldValue("profile_pic_file", null);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };


  const formik = useFormik<FormValues>({
    enableReinitialize: true,
    initialValues: {
      email: "",
      password: "",
      phone_number: "",
      name: "",
      last_name: "",
      deputy_current_year: "",
      deputy_control_number: "",
      is_waved_report_enable: false,
      status: "VERIFIED",
      profile_picture: null,
      profile_pic_file: null,
      card_issued_date: "",
      deputy_endorser: "",
      delete_request: "FALSE",
    },
    validationSchema: getValidationSchema(editMode),
    onSubmit: async (values: FormValues) => {
      try {
        if (editMode) {
          // ✅ Send updated deputy fields including profile picture
          await updateDeputyuser({
            id: currentUser?.id,
            name: values.name,
            last_name: values.last_name,
            phone_number: values.phone_number,
            status: values.status,
            deputy_current_year: values.deputy_current_year,
            deputy_control_number: values.deputy_control_number,
            is_waved_report_enable: values.is_waved_report_enable,
            card_issued_date: values.card_issued_date,
            deputy_endorser: values.deputy_endorser,
            delete_request: values.delete_request,
            profile_picture: values.profile_picture,
            profile_pic_file: values.profile_pic_file,
          }).unwrap();
          Notify("info", "User updated Successfully");
        } else {
          // (add flow unchanged)
          const signUpResponse = await SignUp(values.email, values.password);
          if (signUpResponse) {
            await addDeputyUser({
              ...values,
              role: "DEPUTY",
              sub_role: "DEPUTY",
              status: values.status || "VERIFIED",
              user_id: signUpResponse.user.id,
              gov_issued_ID_url: null,
            });
            Notify("info", "User added Successfully");
          }
        }
        setOpenAddModal(false);
      } catch (error: any) {
        console.error(error);
        Notify("error", error?.message ?? "Failed to save user");
      }
    },
  });

  const columns = [
    {
      id: "rowNumber",
      header: "No.",
      size: 70,
      enableSorting: false,
      Cell: ({ row }: any) => {
        const globalIndex =
          (parentPage - 1) * rowsPerParentPage + row.index + 1;

        return (
          <Typography>
            {globalIndex}
          </Typography>
        );
      },
    },
    {
      accessorKey: "name",
      header: "Deputy",
      size: 260,
      Cell: ({ row }: any) => (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Avatar
            src={
              row.original.profile_picture ||
              "https://smrgejlacnpeerpzxezv.supabase.co/storage/v1/object/public/assets/default-user-image.png"
            }
            alt={row.original.name}
            sx={{ width: 35, height: 35 }}
          />
          <Typography>
            {row.original.last_name}, {row.original.name}
          </Typography>
        </Box>
      ),
    },
    {
      accessorKey: "deputy_endorser",
      header: "Endorser",
      size: 220,
    },
    {
      accessorKey: "deputy_current_year",
      header: "Year",
      size: 100,
    },
    {
      accessorKey: "deputy_control_number",
      header: "Control #",
      size: 140,
    },
    {
      accessorKey: "status",
      header: "Status",
      size: 140,
      Cell: ({ row }: any) => {
        const status = row.original.status as string;
        const label = status === "VERIFIED" ? "ACTIVE" : status || "PENDING";
        return (
          <StatusChip
            label={label}
            tone={label === "ACTIVE" ? "success" : label === "REJECTED" || label === "BLOCKED" ? "error" : "info"}
          />
        );
      },
    },
    {
      id: "actions",
      header: "Actions",
      size: 190,
      enableSorting: false,
      Cell: ({ row }: any) => {
        const user = row.original;
        return (
          <RowActions
            actions={[
              {
                label: "Edit",
                onClick: () => openAddEditModal(user),
              },
              {
                label: "Password",
                onClick: () => {
                  setActionUser(user);
                  setNewPassword("");
                  setConfirmPassword("");
                  setShowChangePassword(false);
                  setChangePasswordOpen(true);
                },
              },
              {
                label: "Email",
                onClick: () => {
                  setActionUser(user);
                  setNewEmail("");
                  setConfirmEmail("");
                  setChangeEmailOpen(true);
                },
              },
            ]}
            inline={2}
          />
        );
      },
    },
  ];


  return (
    <DefaultPage 
      title="Deputy Users" 
      subTitle="Manage and review deputy accounts."
      loading={isLoading}
    >
      <Box mb={2.5}>
        <Alert
          severity="info"
          sx={{
            borderRadius: "6px",
            fontSize: 13,
            border: "1px solid #BAE6FD",
            backgroundColor: "#F0F9FF",
            "& .MuiAlert-icon": { color: "#0072BC" },
          }}
        >
          Displays registered deputy users. Set the filters and click <strong>Search</strong> to find deputies.
        </Alert>
      </Box>

      {isError && (
        <Box mb={2.5}>
          <Alert severity="error" sx={{ borderRadius: "6px", fontSize: 13 }}>
            Failed to load deputy users.
          </Alert>
        </Box>
      )}

      {/* Standardized Gold-Standard Filter Card */}
      <Box
        sx={{
          backgroundColor: "#FFFFFF",
          border: "1px solid #E2E8F0",
          borderRadius: "6px",
          p: 2,
          mb: 2,
          boxShadow: "0 1px 3px 0 rgba(0, 0, 0, 0.04)",
        }}
      >
        <Stack spacing={2}>
          <Stack
            direction={{ xs: "column", md: "row" }}
            spacing={1.5}
            flexWrap="wrap"
            useFlexGap
            alignItems={{ xs: "stretch", md: "center" }}
          >
            <TextField
              select
              size="small"
              label="Year"
              value={selectedYear}
              onChange={(e) => {
                setParentPage(1);
                setSelectedYear(Number(e.target.value));
              }}
              sx={{
                minWidth: 120,
                "& .MuiOutlinedInput-root": { borderRadius: "4px", fontSize: 13.5 },
              }}
            >
              {yearOptions.map((year) => (
                <MenuItem key={year} value={year}>
                  {year}
                </MenuItem>
              ))}
            </TextField>

            <TextField
              select
              size="small"
              label="Sort by"
              value={sortBy}
              onChange={(e) => {
                setParentPage(1);
                setSortBy(e.target.value as any);
              }}
              sx={{
                minWidth: 160,
                "& .MuiOutlinedInput-root": { borderRadius: "4px", fontSize: 13.5 },
              }}
            >
              <MenuItem value="CONTROL">Control Number</MenuItem>
              <MenuItem value="LAST_NAME">Last Name</MenuItem>
            </TextField>

            <Button
              variant="outlined"
              size="small"
              onClick={() => {
                setParentPage(1);
                setSortDir((d) => (d === "asc" ? "desc" : "asc"));
              }}
              sx={{
                height: 40,
                px: 2,
                borderRadius: "4px",
                borderColor: "#CBD5E1",
                color: "#334155",
                fontWeight: 600,
                fontSize: 13,
                textTransform: "none",
                "&:hover": { borderColor: "#94A3B8", backgroundColor: "#F8FAFC" },
              }}
            >
              {sortDir === "asc" ? "Asc ↑" : "Desc ↓"}
            </Button>

            <TextField
              size="small"
              label="Search Name, Control #, Endorser"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              onKeyDown={onSearchKeyDown}
              sx={{
                minWidth: 300,
                "& .MuiOutlinedInput-root": { borderRadius: "4px", fontSize: 13.5 },
              }}
            />
          </Stack>

          <Divider sx={{ borderColor: "#F1F5F9" }} />

          <Stack
            direction={{ xs: "column", sm: "row" }}
            justifyContent="space-between"
            alignItems={{ xs: "stretch", sm: "center" }}
            spacing={1.5}
          >
            <Stack direction="row" spacing={1}>
              <Button
                variant="contained"
                size="small"
                startIcon={<SearchRounded fontSize="small" />}
                onClick={triggerSearch}
                disabled={isFetching}
                sx={{
                  backgroundColor: "#0072BC",
                  color: "#FFFFFF",
                  fontWeight: 600,
                  fontSize: 13,
                  px: 2,
                  py: 0.75,
                  borderRadius: "4px",
                  textTransform: "none",
                  boxShadow: "none",
                  "&:hover": {
                    backgroundColor: "#005a96",
                    boxShadow: "none",
                  },
                }}
              >
                {isFetching ? "Searching..." : "Search"}
              </Button>

              {(searchTerm || searchInput) && (
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<RestartAltRounded fontSize="small" />}
                  onClick={clearSearch}
                  disabled={isFetching}
                  sx={{
                    color: "#64748B",
                    borderColor: "#CBD5E1",
                    textTransform: "none",
                    fontWeight: 600,
                    fontSize: 13,
                    borderRadius: "4px",
                    "&:hover": {
                      borderColor: "#94A3B8",
                      backgroundColor: "#F8FAFC",
                    },
                  }}
                >
                  Clear
                </Button>
              )}
            </Stack>

            <Stack direction="row" spacing={1} flexWrap="wrap">
              <Button
                variant="outlined"
                size="small"
                startIcon={<PictureAsPdfRounded fontSize="small" />}
                onClick={handleExportPDF}
                disabled={isExporting}
                sx={{
                  color: "#334155",
                  borderColor: "#CBD5E1",
                  textTransform: "none",
                  fontWeight: 600,
                  fontSize: 13,
                  borderRadius: "4px",
                  "&:hover": {
                    borderColor: "#94A3B8",
                    backgroundColor: "#F8FAFC",
                  },
                }}
              >
                Export PDF
              </Button>

              <Button
                variant="outlined"
                size="small"
                startIcon={<TableChartRounded fontSize="small" />}
                onClick={handleExportExcel}
                disabled={isExporting}
                sx={{
                  color: "#0072BC",
                  borderColor: "#BAE6FD",
                  backgroundColor: "#F0F9FF",
                  textTransform: "none",
                  fontWeight: 600,
                  fontSize: 13,
                  borderRadius: "4px",
                  "&:hover": {
                    borderColor: "#0072BC",
                    backgroundColor: "#E0F2FE",
                  },
                }}
              >
                Export Excel
              </Button>

              <Button
                variant="contained"
                size="small"
                startIcon={<Add fontSize="small" />}
                onClick={() => openAddEditModal()}
                sx={{
                  backgroundColor: "#00934C",
                  color: "#FFFFFF",
                  fontWeight: 600,
                  fontSize: 13,
                  borderRadius: "4px",
                  textTransform: "none",
                  boxShadow: "none",
                  "&:hover": {
                    backgroundColor: "#007a3f",
                    boxShadow: "none",
                  },
                }}
              >
                Add Deputy
              </Button>
            </Stack>
          </Stack>
        </Stack>
      </Box>

      {/* Matched Records Banner */}
      <Box mb={2}>
        <Alert
          severity="success"
          sx={{
            borderRadius: "6px",
            fontSize: 13,
            border: "1px solid #BBF7D0",
            backgroundColor: "#F0FDF4",
            py: 0.5,
            "& .MuiAlert-icon": { color: "#00934C" },
          }}
        >
          Total matched records: <strong>{Number(totalDeputyCount || 0).toLocaleString()}</strong>
        </Alert>
      </Box>

      <ExportLoadingBackdrop open={isExporting} message={exportMessage} progress={exportProgress} />
      <DataTableFive
        data={deputyUsers}
        total={totalDeputyCount}
        page={parentPage}
        pageSize={rowsPerParentPage}
        onPageChange={handlePageChange}
        columns={columns as any}
        getRowId={(row: { id: any }) => String(row.id)}
        isLoading={isLoading || isFetching}
        onRowClick={(row: any) => openAddEditModal(row.original)}
      />

      {/* Row click action selector */}
      <Dialog
        open={userActionOpen}
        onClose={closeUserActionDialog}
        fullWidth
        maxWidth="xs"
        PaperProps={{ sx: { borderRadius: 1 } }}
      >
        <Box sx={{ position: "relative", p: 2.5, pb: 1 }}>
          <IconButton
            aria-label="close"
            onClick={closeUserActionDialog}
            sx={{ position: "absolute", top: 8, right: 8 }}
          >
            <img alt="close icon" src="/x.png" />
          </IconButton>
          <Typography variant="h6" fontWeight={700}>
            Select Action
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            {actionUser
              ? `${actionUser.last_name ?? ""}, ${actionUser.name ?? ""}`
              : "Deputy User"}
          </Typography>
        </Box>

        <DialogContent dividers sx={{ p: 2.5 }}>
          <Stack spacing={1.5}>
            <Button
              fullWidth
              variant="outlined"
              startIcon={<ManageAccounts />}
              onClick={handleUpdateDeputyAction}
              sx={{
                justifyContent: "flex-start",
                py: 1.25,
                textTransform: "none",
                fontWeight: 700,
              }}
            >
              Update Deputy
            </Button>

            <Button
              fullWidth
              variant="contained"
              startIcon={<LockReset />}
              onClick={handleChangePasswordAction}
              sx={{
                justifyContent: "flex-start",
                py: 1.25,
                textTransform: "none",
                fontWeight: 700,
                bgcolor: "#0054D3",
                "&:hover": { bgcolor: "#003f9e" },
              }}
            >
              Change Password
            </Button>

            <Button
              fullWidth
              variant="contained"
              startIcon={<AlternateEmail />}
              onClick={handleChangeEmailAction}
              sx={{
                justifyContent: "flex-start",
                py: 1.25,
                textTransform: "none",
                fontWeight: 700,
                bgcolor: "#2e7d32",
                "&:hover": { bgcolor: "#1b5e20" },
              }}
            >
              Change Email
            </Button>
          </Stack>
        </DialogContent>
      </Dialog>

      {/* Change password dialog */}
      <Dialog
        open={changePasswordOpen}
        onClose={closeChangePasswordDialog}
        fullWidth
        maxWidth="sm"
        PaperProps={{ sx: { borderRadius: 2 } }}
      >
        <Box sx={{ position: "relative", p: 2.5, pb: 1 }}>
          <IconButton
            aria-label="close"
            onClick={closeChangePasswordDialog}
            disabled={changingPassword}
            sx={{ position: "absolute", top: 8, right: 8 }}
          >
            <img alt="close icon" src="/x.png" />
          </IconButton>
          <Typography variant="h6" fontWeight={700}>
            Change Password
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            {actionUser
              ? `${actionUser.last_name ?? ""}, ${actionUser.name ?? ""}`
              : "Deputy User"}
          </Typography>
        </Box>

        <DialogContent dividers sx={{ p: 2.5 }}>
          <Stack spacing={2}>
            <TextField
              autoFocus
              fullWidth
              label="New Password"
              type={showChangePassword ? "text" : "password"}
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              disabled={changingPassword}
              helperText="Minimum of 8 characters"
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label="toggle new password visibility"
                      onClick={() => setShowChangePassword((prev) => !prev)}
                      edge="end"
                    >
                      {showChangePassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />

            <TextField
              fullWidth
              label="Confirm New Password"
              type={showChangePassword ? "text" : "password"}
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              disabled={changingPassword}
              onKeyDown={(e) => {
                if (e.key === "Enter") submitChangePassword();
              }}
            />
          </Stack>
        </DialogContent>

        <DialogActions sx={{ p: 2.5 }}>
          <Button
            onClick={closeChangePasswordDialog}
            disabled={changingPassword}
            sx={{ textTransform: "none" }}
          >
            Cancel
          </Button>
          <LoadingButton
            variant="contained"
            loading={changingPassword}
            onClick={submitChangePassword}
            sx={{
              textTransform: "none",
              bgcolor: "#0054D3",
              "&:hover": { bgcolor: "#003f9e" },
            }}
          >
            Save Password
          </LoadingButton>
        </DialogActions>
      </Dialog>

      {/* Change Email Dialog */}
      <Dialog
        open={changeEmailOpen}
        onClose={closeChangeEmailDialog}
        fullWidth
        maxWidth="sm"
        PaperProps={{ sx: { borderRadius: 2 } }}
      >
        <Box sx={{ position: "relative", p: 2.5, pb: 1 }}>
          <IconButton
            aria-label="close"
            onClick={closeChangeEmailDialog}
            disabled={changingEmail}
            sx={{ position: "absolute", top: 8, right: 8 }}
          >
            <img alt="close icon" src="/x.png" />
          </IconButton>
          <Typography variant="h6" fontWeight={700}>
            Change Email
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            {actionUser
              ? `${actionUser.last_name ?? ""}, ${actionUser.name ?? ""}`
              : "Deputy User"}
          </Typography>
        </Box>

        <DialogContent dividers sx={{ p: 2.5 }}>
          <Stack spacing={2}>
            {actionUser?.email && (
              <Box sx={{ p: 1.5, bgcolor: "action.hover", borderRadius: 1 }}>
                <Typography variant="caption" color="text.secondary" display="block">
                  Current Email
                </Typography>
                <Typography variant="body2" fontWeight={600}>
                  {actionUser.email}
                </Typography>
              </Box>
            )}
            <TextField
              autoFocus
              fullWidth
              label="New Email Address"
              type="email"
              value={newEmail}
              onChange={(e) => setNewEmail(e.target.value)}
              disabled={changingEmail}
              helperText="Enter the new email address for this deputy"
            />
            <TextField
              fullWidth
              label="Confirm New Email Address"
              type="email"
              value={confirmEmail}
              onChange={(e) => setConfirmEmail(e.target.value)}
              disabled={changingEmail}
              onKeyDown={(e) => {
                if (e.key === "Enter") submitChangeEmail();
              }}
            />
          </Stack>
        </DialogContent>

        <DialogActions sx={{ p: 2.5 }}>
          <Button
            onClick={closeChangeEmailDialog}
            disabled={changingEmail}
            sx={{ textTransform: "none" }}
          >
            Cancel
          </Button>
          <LoadingButton
            variant="contained"
            loading={changingEmail}
            onClick={submitChangeEmail}
            sx={{
              textTransform: "none",
              bgcolor: "#2e7d32",
              "&:hover": { bgcolor: "#1b5e20" },
            }}
          >
            Save Email
          </LoadingButton>
        </DialogActions>
      </Dialog>

      <Dialog
        open={openAddModal}
        onClose={closeAddEditModal}
        fullWidth
        maxWidth="lg"
        scroll="paper"
        PaperProps={{
          sx: {
            borderRadius: 1,
            maxWidth: 1000,
            maxHeight: '90vh',   // still cap whole dialog
          },
        }}
      >
        <Box sx={{ position: 'relative', p: 2.5, pb: 1 }}>
          <IconButton
            aria-label="close"
            onClick={closeAddEditModal}
            sx={{ position: 'absolute', top: 8, right: 8 }}
          >
            <img alt="close icon" src="/x.png" />
          </IconButton>
          <Typography variant="h6">
            {editMode ? "Edit Deputy User" : "Add Deputy User"}
          </Typography>
        </Box>

        <form noValidate onSubmit={formik.handleSubmit}>
          <DialogContent
            dividers
            sx={{
              pt: 1,
              pb: 2,
              px: 2.5,
              maxHeight: '65vh',   // 👈 cap height here
              overflowY: 'auto',   // 👈 scroll here
            }}
          >
            <Grid container spacing={2}>
              {/* Row 1 */}
              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <label htmlFor="deputy_current_year" style={{ color: "#AFAFAF", marginBottom: 7 }}>
                    Deputy Year
                  </label>
                  <TextField
                    name="deputy_current_year"
                    id="deputy_current_year"
                    variant="outlined"
                    value={formik.values.deputy_current_year}
                    onChange={formik.handleChange}
                    type="number"
                    onBlur={formik.handleBlur}
                    error={formik.touched.deputy_current_year && Boolean(formik.errors.deputy_current_year)}
                    helperText={formik.touched.deputy_current_year && (formik.errors.deputy_current_year as any)}
                  // disabled={editMode}
                  />
                </FormControl>
              </Grid>

              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <label htmlFor="deputy_control_number" style={{ color: "#AFAFAF", marginBottom: 7 }}>
                    Deputy Control Number
                  </label>
                  <TextField
                    name="deputy_control_number"
                    id="deputy_control_number"
                    variant="outlined"
                    value={formik.values.deputy_control_number}
                    onChange={formik.handleChange}
                    type="number"
                    onBlur={formik.handleBlur}
                    error={formik.touched.deputy_control_number && Boolean(formik.errors.deputy_control_number)}
                    helperText={formik.touched.deputy_control_number && (formik.errors.deputy_control_number as any)}
                  // disabled={editMode}
                  />
                </FormControl>
              </Grid>

              {/* Row 2 */}
              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <label htmlFor="name" style={{ color: "#AFAFAF", marginBottom: 7 }}>
                    Firstname
                  </label>
                  <TextField
                    name="name"
                    id="name"
                    variant="outlined"
                    value={formik.values.name}
                    onChange={formik.handleChange}
                    type="text"
                    onBlur={formik.handleBlur}
                    error={formik.touched.name && Boolean(formik.errors.name)}
                    helperText={formik.touched.name && (formik.errors.name as any)}
                  />
                </FormControl>
              </Grid>

              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <label htmlFor="last_name" style={{ color: "#AFAFAF", marginBottom: 7 }}>
                    Lastname
                  </label>
                  <TextField
                    name="last_name"
                    id="last_name"
                    variant="outlined"
                    value={formik.values.last_name}
                    onChange={formik.handleChange}
                    type="text"
                    onBlur={formik.handleBlur}
                    error={formik.touched.last_name && Boolean(formik.errors.last_name)}
                    helperText={formik.touched.last_name && (formik.errors.last_name as any)}
                  />
                </FormControl>
              </Grid>

              {/* Row 3 */}
              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <label htmlFor="email" style={{ color: "#AFAFAF", marginBottom: 7 }}>
                    Email
                  </label>
                  <TextField
                    name="email"
                    id="email"
                    variant="outlined"
                    value={formik.values.email}
                    onChange={formik.handleChange}
                    type="email"
                    onBlur={formik.handleBlur}
                    error={formik.touched.email && Boolean(formik.errors.email)}
                    helperText={formik.touched.email && (formik.errors.email as any)}
                    disabled={editMode}
                  />
                </FormControl>
              </Grid>

              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <label htmlFor="phone_number" style={{ color: "#AFAFAF", marginBottom: 7 }}>
                    Phone Number
                  </label>
                  <TextField
                    name="phone_number"
                    id="phone_number"
                    variant="outlined"
                    value={formik.values.phone_number}
                    onChange={formik.handleChange}
                    type="text"
                    onBlur={formik.handleBlur}
                    error={formik.touched.phone_number && Boolean(formik.errors.phone_number)}
                    helperText={formik.touched.phone_number && (formik.errors.phone_number as any)}
                  />
                </FormControl>
              </Grid>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <label style={{ color: "#AFAFAF", marginBottom: 7 }}>
                    Endorser
                  </label>
                  <TextField
                    name="deputy_endorser"
                    value={formik.values.deputy_endorser}
                    onChange={(e) =>
                      formik.setFieldValue(
                        "deputy_endorser",
                        e.target.value.toUpperCase() // ✅ FORCE CAPS
                      )
                    }
                    onBlur={formik.handleBlur}
                    error={
                      formik.touched.deputy_endorser &&
                      Boolean(formik.errors.deputy_endorser)
                    }
                    helperText={
                      formik.touched.deputy_endorser &&
                      formik.errors.deputy_endorser
                    }
                  />
                </FormControl>
              </Grid>

              {/* Row 4: Password only when adding */}
              {!editMode && (
                <Grid item xs={12}>
                  <FormControl fullWidth>
                    <label htmlFor="password" style={{ color: "#AFAFAF", marginBottom: 7 }}>
                      Password
                    </label>
                    <TextField
                      name="password"
                      id="password"
                      variant="outlined"
                      value={formik.values.password}
                      onChange={formik.handleChange}
                      type={showPassword ? "text" : "password"}
                      onBlur={formik.handleBlur}
                      error={formik.touched.password && Boolean(formik.errors.password)}
                      helperText={formik.touched.password && (formik.errors.password as any)}
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton aria-label="toggle password visibility" onClick={handleTogglePasswordVisibility} edge="end">
                              {showPassword ? <VisibilityOff /> : <Visibility />}
                            </IconButton>
                          </InputAdornment>
                        ),
                      }}
                    />
                  </FormControl>
                </Grid>
              )}

              {/* Row 5 */}
              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <FormControlLabel
                    control={
                      <Checkbox
                        name="is_waved_report_enable"
                        checked={formik.values.is_waved_report_enable}
                        onChange={formik.handleChange}
                        color="primary"
                      />
                    }
                    label="Exempted from Reporting (No Reports)"
                  />
                </FormControl>
              </Grid>

              <Grid item xs={12} md={6}>
                {editMode && (
                  <FormControl fullWidth size="small">
                    <InputLabel id="status-label" sx={{ fontSize: 14 }}>
                      Status
                    </InputLabel>
                    <Select
                      labelId="status-label"
                      id="status"
                      name="status"
                      value={formik.values.status}
                      onChange={(e) => formik.setFieldValue("status", e.target.value, true)}
                      onBlur={formik.handleBlur}
                      label="Status"
                      error={formik.touched.status && Boolean(formik.errors.status)}
                      sx={{ fontSize: 14, ".MuiSelect-select": { py: 1.2 } }}
                    >
                      <MenuItem value="VERIFIED">
                        <Box display="flex" alignItems="center" gap={1}>
                          <CheckCircle fontSize="small" sx={{ color: "#0054D3" }} />
                          <Typography fontSize={14} color="#0054D3">ACTIVE</Typography>
                        </Box>
                      </MenuItem>
                      <MenuItem value="BLOCKED">
                        <Box display="flex" alignItems="center" gap={1}>
                          <BlockIcon fontSize="small" sx={{ color: "#B00020" }} />
                          <Typography fontSize={14} color="#B00020">BLOCK</Typography>
                        </Box>
                      </MenuItem>
                    </Select>
                    {formik.touched.status && formik.errors.status && (
                      <Typography variant="caption" color="error" sx={{ fontSize: 12, mt: 0.5, ml: 1 }}>
                        {formik.errors.status as string}
                      </Typography>
                    )}
                  </FormControl>
                )}
              </Grid>
              <Grid item xs={12} md={6}>
                {editMode && (
                  <FormControl fullWidth size="small">
                    <InputLabel id="delete-request-label" sx={{ fontSize: 14 }}>
                      Delete Request
                    </InputLabel>
                    <Select
                      labelId="delete-request-label"
                      id="delete_request"
                      name="delete_request"
                      value={formik.values.delete_request}
                      onChange={(e) => formik.setFieldValue("delete_request", e.target.value, true)}
                      onBlur={formik.handleBlur}
                      label="Delete Request"
                      error={formik.touched.delete_request && Boolean(formik.errors.delete_request)}
                      sx={{ fontSize: 14, ".MuiSelect-select": { py: 1.2 } }}
                    >
                      <MenuItem value="APPROVED">
                        <Box display="flex" alignItems="center" gap={1}>
                          <CheckCircle fontSize="small" sx={{ color: "#B00020" }} />
                          <Typography fontSize={14} color="#B00020">APPROVE</Typography>
                        </Box>
                      </MenuItem>
                      <MenuItem value="FALSE">
                        <Box display="flex" alignItems="center" gap={1}>
                          <BlockIcon fontSize="small" sx={{ color: "#0054D3" }} />
                          <Typography fontSize={14} color="#0054D3">DISREGARD</Typography>
                        </Box>
                      </MenuItem>
                      <MenuItem value="PENDING">
                        <Box display="flex" alignItems="center" gap={1}>
                          <PendingActions fontSize="small" sx={{ color: "#FF9800" }} />
                          <Typography fontSize={14} color="#FF9800">PENDING</Typography>
                        </Box>
                      </MenuItem>
                    </Select>
                    {formik.touched.delete_request && formik.errors.delete_request && (
                      <Typography variant="caption" color="error" sx={{ fontSize: 12, mt: 0.5, ml: 1 }}>
                        {formik.errors.delete_request as string}
                      </Typography>
                    )}
                  </FormControl>
                )}
              </Grid>

              {/* Row 6: ID Picture — compact card */}
              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <label htmlFor="profile_picture" style={{ color: "#AFAFAF", marginBottom: 7 }}>
                    ID Picture
                  </label>

                  <Box
                    onClick={handleChooseClick}
                    onDrop={onDrop}
                    onDragOver={onDragOver}
                    onDragLeave={onDragLeave}
                    sx={{
                      position: "relative",
                      width: 150,
                      height: 150,
                      overflow: "hidden",
                      cursor: "pointer",
                      border: "1px dashed",
                      borderColor:
                        formik.touched.profile_pic_file && formik.errors.profile_pic_file
                          ? "error.main"
                          : picPreviewUrl ? "transparent" : "divider",
                      bgcolor: "background.default",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      mx: "auto",
                    }}
                  >
                    {picPreviewUrl ? (
                      <>
                        <Box
                          component="img"
                          src={picPreviewUrl}
                          alt="ID Picture"
                          sx={{
                            width: "100%",
                            height: "100%",
                            objectFit: "cover",
                          }}
                        />
                        <IconButton
                          size="small"
                          onClick={(e) => { e.stopPropagation(); clearPreview(); }}
                          sx={{
                            position: "absolute",
                            top: 4,
                            right: 4,
                            bgcolor: "rgba(0,0,0,0.55)",
                            color: "common.white",
                            "&:hover": { bgcolor: "rgba(0,0,0,0.75)" },
                          }}
                        >
                          <DeleteOutlined fontSize="small" />
                        </IconButton>
                      </>
                    ) : (
                      <Box sx={{ textAlign: "center", color: "text.secondary" }}>
                        <ImageOutlined sx={{ fontSize: 32, opacity: 0.7 }} />
                        <Typography fontSize={12}>
                          Upload
                        </Typography>
                      </Box>
                    )}

                    <input
                      ref={fileInputRef}
                      type="file"
                      accept="image/*"
                      hidden
                      onChange={onInputChange}
                    />
                  </Box>
                </FormControl>
              </Grid>



              {/* Row 7: Card Issued Date (non-editable when editing) */}
              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <label htmlFor="card_issued_date" style={{ color: "#AFAFAF", marginBottom: 7 }}>
                    Card Issued Date{!editMode && <span style={{ color: '#B00020' }}> *</span>}
                  </label>
                  <TextField
                    name="card_issued_date"
                    id="card_issued_date"
                    variant="outlined"
                    value={formik.values.card_issued_date}
                    onChange={formik.handleChange}
                    type="date"
                    onBlur={formik.handleBlur}
                    error={formik.touched.card_issued_date && Boolean(formik.errors.card_issued_date)}
                    helperText={formik.touched.card_issued_date && (formik.errors.card_issued_date as any)}
                  // disabled={editMode}
                  />
                </FormControl>
              </Grid>
            </Grid>
          </DialogContent>

          <DialogActions
            sx={{
              p: 2.5,
              pt: 1.5,
              bgcolor: 'background.paper',
              borderTop: (t) => `1px solid ${t.palette.divider}`,
            }}
          >
            <LoadingButton
              type="submit"
              loading={addDeputyUserLoading || updating || formik.isSubmitting}
              variant="contained"
              sx={{
                textTransform: 'none',
                borderRadius: '10px',
                px: 3,              // padding left/right
                bgcolor: '#0054D3',
                color: 'white',
                '&.Mui-disabled': { bgcolor: '#d3d3d3', color: '#888' },
                '&:hover': { bgcolor: '#5489de' },
              }}
            >
              Save
            </LoadingButton>
          </DialogActions>
        </form>
      </Dialog>


      <ConfirmationDialogue
        open={openConfirm}
        onClose={() => setOpenConfirm(false)}
        onConfirm={blockUser}
        loading={blockLoading}
        description={`Do you really want to ${blockState ? "block" : "unblock"} ${selectedItem?.name ?? "this user"}?`}
        title={blockState ? "Confirm Block" : "Confirm Unblock"}
      />
    </DefaultPage >
  );
}
